/**
 * react-select, loaded and rendered in the browser only. Import react-select
 * through this module, never directly.
 *
 * The server renders pages with the client bundle, and webpack builds that
 * bundle with react-select's and emotion's browser builds. react-select's
 * reads window when it loads and emotion's needs document to render, so the
 * server must do neither (scripts/check-server-bundle.js fails the build when
 * the server can't load the bundle).
 *
 * The server, and the browser's first render so that hydration matches, show
 * a static copy of the closed control. It uses react-select's class names
 * (classNamePrefix), so the caller's CSS styles it like the real control. The
 * `styles` prop is not applied to it, so style the control with CSS.
 */
import React, { forwardRef, useEffect, useLayoutEffect, useState } from 'react';
import classNames from 'classnames';

import css from './ReactSelect.css';

const reactSelect = typeof window === 'undefined' ? null : require('react-select');

// react-select's building blocks, for custom components. Empty on the server,
// where custom components never render.
export const selectComponents = reactSelect ? reactSelect.components : {};

// React warns about useLayoutEffect during server rendering.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// Once the app has hydrated, later mounts show react-select straight away.
let hasHydrated = false;

const useHasHydrated = () => {
  const [hydrated, setHydrated] = useState(hasHydrated);
  // A layout effect swaps in react-select before the browser paints.
  useIsomorphicLayoutEffect(() => {
    hasHydrated = true;
    setHydrated(true);
  }, []);
  return hydrated;
};

// react-select's default placeholder.
const DEFAULT_PLACEHOLDER = 'Select...';

const optionLabel = (option, getOptionLabel) => {
  if (!option || typeof option !== 'object') {
    return null;
  }
  return getOptionLabel ? getOptionLabel(option) : option.label;
};

const DropdownIcon = () => (
  <svg
    className={css.dropdownIcon}
    height="20"
    width="20"
    viewBox="0 0 20 20"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
  </svg>
);

const StaticControl = props => {
  const {
    id,
    className,
    classNamePrefix,
    placeholder,
    value,
    defaultValue,
    getOptionLabel,
    components,
    isDisabled,
  } = props;

  // The same class names react-select gives its parts.
  const prefixed = (...names) =>
    classNamePrefix ? names.map(name => `${classNamePrefix}${name}`) : null;

  const selected = value !== undefined ? value : defaultValue;
  const label = (Array.isArray(selected) ? selected : [selected])
    .map(option => optionLabel(option, getOptionLabel))
    .filter(Boolean)
    .join(', ');
  // A replaced part is drawn by the caller's component, which only exists in
  // react-select, so the copy leaves it out.
  const showDropdownIndicator = !(components && components.DropdownIndicator);
  const showSeparator = showDropdownIndicator && !(components && components.IndicatorSeparator);

  return (
    <div
      id={id}
      className={classNames(css.container, className, prefixed(isDisabled && '--is-disabled'))}
    >
      <div
        className={classNames(
          css.control,
          { [css.controlDisabled]: isDisabled },
          prefixed('__control', isDisabled && '__control--is-disabled')
        )}
      >
        <div className={classNames(css.valueContainer, prefixed('__value-container'))}>
          {label ? (
            <div className={classNames(css.singleValue, prefixed('__single-value'))}>{label}</div>
          ) : (
            <div className={classNames(css.placeholder, prefixed('__placeholder'))}>
              {placeholder === undefined ? DEFAULT_PLACEHOLDER : placeholder}
            </div>
          )}
        </div>
        {showDropdownIndicator ? (
          <div className={classNames(css.indicators, prefixed('__indicators'))}>
            {showSeparator ? (
              <span
                className={classNames(
                  css.indicatorSeparator,
                  { [css.indicatorSeparatorDisabled]: isDisabled },
                  prefixed('__indicator-separator')
                )}
              />
            ) : null}
            <div
              className={classNames(
                css.dropdownIndicator,
                prefixed('__indicator', '__dropdown-indicator')
              )}
            >
              <DropdownIcon />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ReactSelect = forwardRef((props, ref) => {
  const hydrated = useHasHydrated();
  if (!reactSelect || !hydrated) {
    return <StaticControl {...props} />;
  }
  const Select = reactSelect.default;
  return <Select ref={ref} {...props} />;
});

ReactSelect.displayName = 'ReactSelect';

export default ReactSelect;
